import { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Check, X, BarChart3, Clock, Newspaper, Mail, ImageIcon, Users, Lock, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_KEY_STORAGE = 'bitcoinAtlasAdminKey'; // sessionStorage: some só nessa aba, nunca fica salvo no código

const AdminPanel = () => {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [pendingNews, setPendingNews] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState({ total: 0, recent: [] });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [regenImages, setRegenImages] = useState(false);
  const [sendingNl, setSendingNl] = useState(false);

  const authHeaders = { headers: { 'X-Admin-Key': adminKey } };

  const handleAuthError = (error) => {
    if (error?.response?.status === 401) {
      sessionStorage.removeItem(ADMIN_KEY_STORAGE);
      setAdminKey('');
      toast.error('Chave de administrador inválida ou expirada. Digite novamente.');
      return true;
    }
    return false;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    sessionStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim());
    setAdminKey(keyInput.trim());
    setKeyInput('');
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey('');
  };

  useEffect(() => {
    if (!adminKey) {
      setLoading(false);
      return;
    }
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  const fetchData = async () => {
    try {
      const [newsRes, statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-news`, authHeaders),
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/admin/newsletter-subscribers`, authHeaders),
      ]);
      setPendingNews(newsRes.data);
      setStats(statsRes.data);
      setSubscribers(subsRes.data);
    } catch (error) {
      if (!handleAuthError(error)) {
        console.error('Error fetching admin data:', error);
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (newsId) => {
    try {
      await axios.post(`${API}/admin/approve-news/${newsId}`, null, authHeaders);
      toast.success('Notícia aprovada e publicada!');
      fetchData();
    } catch (error) {
      if (!handleAuthError(error)) toast.error('Erro ao aprovar notícia');
    }
  };

  const handleReject = async (newsId) => {
    try {
      await axios.delete(`${API}/admin/reject-news/${newsId}`, authHeaders);
      toast.success('Notícia rejeitada');
      fetchData();
    } catch (error) {
      if (!handleAuthError(error)) toast.error('Erro ao rejeitar notícia');
    }
  };

  const handleForceUpdate = async () => {
    setUpdating(true);
    try {
      const response = await axios.post(`${API}/admin/force-update`, null, authHeaders);
      toast.success(response.data.message);
      setTimeout(fetchData, 3000);
    } catch (error) {
      if (!handleAuthError(error)) toast.error('Erro ao forçar atualização');
    } finally {
      setUpdating(false);
    }
  };

  const handleRegenImages = async () => {
    setRegenImages(true);
    try {
      const response = await axios.post(`${API}/admin/regenerate-images?limit=20`, null, authHeaders);
      toast.success(`${response.data.updated} imagens regeneradas com IA`);
      setTimeout(fetchData, 3000);
    } catch (error) {
      if (!handleAuthError(error)) toast.error('Erro ao regenerar imagens');
    } finally {
      setRegenImages(false);
    }
  };

  const handleSendNewsletter = async (dryRun = false) => {
    setSendingNl(true);
    try {
      const response = await axios.post(`${API}/admin/send-daily-newsletter?dry_run=${dryRun}`, null, authHeaders);
      const r = response.data;
      if (r.error) {
        toast.error(r.error);
      } else if (dryRun) {
        toast.success(`Dry run OK · ${r.subscribers} assinante(s) elegível(is)`);
      } else {
        toast.success(`Newsletter enviada · ${r.sent} ok / ${r.skipped} falhas`);
      }
    } catch (error) {
      if (!handleAuthError(error)) toast.error('Erro ao disparar newsletter');
    } finally {
      setSendingNl(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm bg-card border border-border rounded-sm p-8 space-y-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lock size={20} className="text-primary" />
            <h1 className="text-xl font-bold">Painel Administrativo</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Digite a chave de administrador para continuar. Ela fica salva só
            nesta aba, nunca é gravada no código do site.
          </p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Chave de administrador"
            autoFocus
            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
            data-testid="admin-key-input"
          />
          <Button type="submit" className="w-full" data-testid="admin-key-submit">
            Entrar
          </Button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Carregando painel admin...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
              Painel Administrativo
            </h1>
            <p className="text-muted-foreground">
              Gerencie notícias agregadas, imagens e newsletter
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleForceUpdate}
              disabled={updating}
              className="bg-primary text-primary-foreground"
              data-testid="force-update-button"
            >
              <RefreshCw className={`mr-2 ${updating ? 'animate-spin' : ''}`} size={16} />
              {updating ? 'Atualizando...' : 'Buscar notícias'}
            </Button>
            <Button
              onClick={handleRegenImages}
              disabled={regenImages}
              variant="secondary"
              data-testid="regen-images-button"
            >
              <ImageIcon className={`mr-2 ${regenImages ? 'animate-pulse' : ''}`} size={16} />
              {regenImages ? 'Gerando...' : 'Regenerar imagens (IA)'}
            </Button>
            <Button
              onClick={() => handleSendNewsletter(false)}
              disabled={sendingNl}
              variant="outline"
              data-testid="send-newsletter-button"
            >
              <Mail className={`mr-2 ${sendingNl ? 'animate-pulse' : ''}`} size={16} />
              {sendingNl ? 'Enviando...' : 'Enviar newsletter'}
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              title="Sair (esquecer chave de administrador nesta aba)"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>

        {/* Estatísticas */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                  <Clock className="text-primary" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.pending_news}</div>
                  <div className="text-sm text-muted-foreground">Pendentes</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                  <Newspaper className="text-green-500" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.published_news}</div>
                  <div className="text-sm text-muted-foreground">Notícias Publicadas</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <BarChart3 className="text-blue-500" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.total_articles}</div>
                  <div className="text-sm text-muted-foreground">Total de Artigos</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-sm p-6" data-testid="subscribers-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-sm flex items-center justify-center">
                  <Users className="text-amber-500" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold">{subscribers.total}</div>
                  <div className="text-sm text-muted-foreground">Newsletter</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notícias Pendentes */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Notícias Pendentes de Aprovação</h2>
          
          {pendingNews.length === 0 ? (
            <div className="bg-card border border-border rounded-sm p-12 text-center">
              <Newspaper className="mx-auto mb-4 text-muted-foreground" size={48} />
              <p className="text-muted-foreground">
                Nenhuma notícia pendente no momento.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                O sistema busca novas notícias automaticamente a cada 3 horas.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingNews.map((news) => (
                <div
                  key={news.hash}
                  className="bg-card border border-border rounded-sm p-6"
                  data-testid={`pending-news-${news.hash}`}
                >
                  <div className="flex gap-6">
                    <div className="relative w-48 h-32 flex-shrink-0">
                      <img
                        src={news.featured_image}
                        alt={news.image_alt || news.title}
                        className="w-full h-full object-cover rounded-sm"
                      />
                      {news.image_source && (
                        <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm">
                          {news.image_source === 'og_image' && '🔗 Original'}
                          {news.image_source === 'unsplash' && '📷 Unsplash'}
                          {news.image_source === 'ai_generated' && '🤖 IA'}
                          {news.image_source === 'default' && '⭐ Padrão'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2">{news.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            Fonte: {news.source_name} • {formatDate(news.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {news.excerpt}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {news.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="bg-primary/10 text-primary px-2 py-1 rounded-sm text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleApprove(news.hash)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                          data-testid={`approve-${news.hash}`}
                        >
                          <Check size={18} className="mr-2" />
                          Aprovar e Publicar
                        </Button>
                        <Button
                          onClick={() => handleReject(news.hash)}
                          variant="destructive"
                          data-testid={`reject-${news.hash}`}
                        >
                          <X size={18} className="mr-2" />
                          Rejeitar
                        </Button>
                        <a
                          href={news.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm flex items-center"
                        >
                          Ver fonte original →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 bg-muted border border-border rounded-sm p-6">
          <h3 className="font-bold mb-2">ℹ️ Como o sistema funciona</h3>
          <ul className="text-sm text-muted-foreground space-y-1.5">
            <li>• <strong>Coleta automática:</strong> agregação de notícias a cada 3 horas a partir de Portal do Bitcoin, Livecoins, Cointimes, Criptofácil, Bitcoin Magazine e Cointelegraph.</li>
            <li>• <strong>Reescrita por IA:</strong> Claude Sonnet 4.6 transforma cada notícia em uma matéria casual e didática em português brasileiro.</li>
            <li>• <strong>Imagens com IA:</strong> Gemini Nano Banana gera uma ilustração editorial exclusiva para cada notícia (com fallback para a imagem original da fonte).</li>
            <li>• <strong>Newsletter diária:</strong> envio automático às 8h (BRT) com as 3 melhores notícias do dia + preço BTC + índice de medo e ganância. Requer <code className="bg-background px-1 rounded">RESEND_API_KEY</code> configurada.</li>
            <li>• <strong>Publicação direta:</strong> nesta versão, as notícias são publicadas diretamente sem fila de aprovação.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;